// CampaignsPage — Listings & Marketing → Campaigns (/dashboard/campaigns).
//   Campaigns:   the list (status, auto-send, wording approval, numbers), the editor (who it
//                is for, what enrols people, when it stops, the email / text steps with the
//                wording check), the managing broker's approval and auto-send switch, a
//                preview, and the people enrolled (enrol from your book, pause, resume,
//                remove, history).
//   Newsletter:  the monthly market newsletter — draft, edit, approve (managing broker), send.
//   Settings:    the site switch (managing broker): OFF until he turns it on.
// Every message goes through the Outbox (lib/outbound): Contact Compliance, quiet hours, the
// Florida 3-per-24h rule, the CAN-SPAM footer and "Reply STOP" apply to each one.
// API: /api/campaigns (routes/dripCampaigns.js).
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const inp = 'border rounded px-2 py-1 text-sm';
const btn = 'px-3 py-1 rounded text-sm border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50';
const btnDark = 'px-3 py-1 rounded text-sm bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50';
const btnGreen = 'px-3 py-1 rounded text-sm bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-50';
const STATUS_STYLE = {
    draft: 'bg-slate-100 text-slate-700', active: 'bg-emerald-100 text-emerald-800', paused: 'bg-amber-100 text-amber-800',
    archived: 'bg-slate-200 text-slate-500', completed: 'bg-blue-100 text-blue-800', stopped: 'bg-rose-100 text-rose-800',
    removed: 'bg-slate-200 text-slate-500', sent: 'bg-emerald-100 text-emerald-800', sending: 'bg-blue-100 text-blue-800',
    approved: 'bg-blue-100 text-blue-800', cancelled: 'bg-slate-200 text-slate-500',
};
const Badge = ({ s, children }) => <span className={`inline-block px-2 py-0.5 rounded text-xs ${STATUS_STYLE[s] || 'bg-slate-100 text-slate-700'}`}>{children || s}</span>;
const errText = (e) => [e.message, ...(e.problems || [])].filter((x, i, a) => x && a.indexOf(x) === i).join(' ');
const when = (d) => (d ? new Date(d).toLocaleString() : '');
const SEV = { block: ['Must fix', 'text-rose-700'], warn: ['Check', 'text-amber-700'], info: ['Note', 'text-slate-500'] };

function Findings({ findings }) {
    if (!findings || !findings.length) return <p className="text-xs text-emerald-700">Wording check: nothing flagged.</p>;
    return (
        <ul className="text-xs space-y-1">
            {findings.map((f, i) => {
                const [label, cls] = SEV[f.severity] || SEV.info;
                return <li key={i} className={cls}><b>{label}:</b> {f.term ? <code>{f.term}</code> : null} {f.why}{f.suggestion ? ` — ${f.suggestion}` : ''}</li>;
            })}
        </ul>
    );
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------
function CampaignList({ api, meta, onOpen }) {
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState('');
    const [run, setRun] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        try { setRows(await api.get('/api/campaigns')); setErr(''); } catch (e) { setErr(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);
    const create = async () => {
        const name = window.prompt('Name of the new campaign');
        if (!name) return;
        try {
            const r = await api.post('/api/campaigns', { name, audience_type: 'new_buyer_lead', trigger_type: 'manual' });
            onOpen(r.campaign.campaign_id);
        } catch (e) { setErr(errText(e)); }
    };
    const runNow = async (dry) => {
        if (!dry && !window.confirm('Run campaigns now? Messages due today are created in the Outbox (awaiting approval unless the campaign is on auto-send).')) return;
        setBusy(true);
        try { setRun(await api.post('/api/campaigns/run', { dry_run: dry })); load(); } catch (e) { setErr(errText(e)); }
        setBusy(false);
    };
    const manage = meta.access.manage;
    return (
        <div className="space-y-3">
            {err && <p className="text-sm text-rose-700">{err}</p>}
            {manage && (
                <div className="flex flex-wrap gap-2">
                    <button className={btnDark} onClick={create}>New campaign</button>
                    <button className={btn} disabled={busy} onClick={() => runNow(true)}>What would run today? (dry run)</button>
                    <button className={btn} disabled={busy || !Number(meta.settings.runner_enabled)} onClick={() => runNow(false)}
                        title={Number(meta.settings.runner_enabled) ? '' : 'Campaigns are switched off on this site (Settings tab)'}>Run now</button>
                </div>
            )}
            {run && (
                <div className="p-3 bg-slate-50 border rounded text-sm">
                    {(run.lines || []).length ? run.lines.map((l, i) => <div key={i}>{l}</div>) : <div>Nothing was due.</div>}
                    {(run.escalations || []).map((l, i) => <div key={`e${i}`} className="text-amber-800">{l}</div>)}
                </div>
            )}
            {!rows ? <p className="text-sm text-slate-500">Loading…</p> : !rows.length ? (
                <p className="text-sm text-slate-500">{manage ? 'No campaigns yet.' : 'No active campaigns to enrol people in.'}</p>
            ) : (
                <DataList rows={rows} rowKey="campaign_id" csvName="campaigns" defaultSort={{ key: 'name', dir: 'asc' }} onRowClick={(c) => onOpen(c.campaign_id)}
                    filters={[{ key: 'campaign_status', label: 'Status' }, { key: 'sending', label: 'Sending' }, { key: 'wording', label: 'Wording' }, { key: 'audience', label: 'Audience' }]}
                    columns={[
                        { key: 'name', label: 'Campaign', render: (c) => <b>{c.name}</b> },
                        { key: 'campaign_status', label: 'Status', render: (c) => <Badge s={c.campaign_status} /> },
                        { key: 'sending', label: 'Sending', value: (c) => (Number(c.auto_send) ? 'auto-send' : 'approval in Outbox'),
                            render: (c) => (Number(c.auto_send) ? <Badge s="active">auto-send</Badge> : <Badge s="draft">approval in Outbox</Badge>) },
                        { key: 'wording', label: 'Wording', value: (c) => (c.wording && c.wording.approved ? 'approved' : c.wording && c.wording.changed ? 'changed — needs approval' : 'not approved'),
                            render: (c) => (c.wording && c.wording.approved ? <Badge s="sent">approved</Badge>
                                : c.wording && c.wording.changed ? <Badge s="stopped">changed — needs approval</Badge> : <Badge s="paused">not approved</Badge>) },
                        { key: 'audience', label: 'Audience', value: (c) => meta.audiences[c.audience_type] || c.audience_type },
                        { key: 'trigger', label: 'Enrols on', value: (c) => meta.triggers[c.trigger_type] || c.trigger_type },
                        { key: 'step_count', label: 'Steps', type: 'number' },
                        { key: 'active', label: 'Active', type: 'number', value: (c) => (c.stats ? Number(c.stats.active) : '') },
                        { key: 'completed', label: 'Completed', type: 'number', value: (c) => (c.stats ? Number(c.stats.completed) : '') },
                        { key: 'sent', label: 'Sent', type: 'number', value: (c) => (c.stats ? Number(c.stats.sent) : '') },
                        { key: 'awaiting', label: 'Awaiting approval', type: 'number', value: (c) => (c.stats ? Number(c.stats.awaiting_approval) : '') },
                        { key: 'replied', label: 'Replied', type: 'number', value: (c) => (c.stats ? Number(c.stats.replied) : '') },
                        { key: 'opted_out', label: 'Opted out', type: 'number', value: (c) => (c.stats ? Number(c.stats.unsubscribed) : '') },
                    ]} />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------
const blankStep = (channel) => ({ channel, wait_days: 2, send_window: 'morning', subject: channel === 'email' ? '' : null, body: '', is_active: 1 });

function StepEditor({ meta, steps, setSteps, readOnly, lint }) {
    const set = (i, patch) => setSteps(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
    const move = (i, d) => {
        const j = i + d;
        if (j < 0 || j >= steps.length) return;
        const next = [...steps];
        [next[i], next[j]] = [next[j], next[i]];
        setSteps(next);
    };
    return (
        <div className="space-y-3">
            {steps.map((s, i) => (
                <div key={i} className={`p-3 border rounded ${Number(s.is_active) ? 'bg-white' : 'bg-slate-50 opacity-70'}`}>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <b>Step {i + 1}</b>
                        <select disabled={readOnly} className={inp} value={s.channel} onChange={(e) => set(i, { channel: e.target.value, subject: e.target.value === 'email' ? (s.subject || '') : null })}>
                            <option value="email">Email</option><option value="sms">Text</option>
                        </select>
                        <label>wait <input disabled={readOnly} type="number" min="0" max="365" className={`${inp} w-16`} value={s.wait_days}
                            onChange={(e) => set(i, { wait_days: e.target.value })} /> day(s) {i === 0 ? 'after enrolment' : 'after the previous step'}</label>
                        <select disabled={readOnly} className={inp} value={s.send_window} onChange={(e) => set(i, { send_window: e.target.value })}>
                            {Object.entries(meta.send_windows).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </select>
                        <label className="flex items-center gap-1"><input disabled={readOnly} type="checkbox" checked={!!Number(s.is_active)}
                            onChange={(e) => set(i, { is_active: e.target.checked ? 1 : 0 })} /> on</label>
                        {!readOnly && (
                            <span className="ml-auto flex gap-1">
                                <button className={btn} onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                                <button className={btn} onClick={() => move(i, 1)} disabled={i === steps.length - 1} aria-label="Move down">↓</button>
                                <button className={btn} onClick={() => setSteps(steps.filter((_, j) => j !== i))}>Remove</button>
                            </span>
                        )}
                    </div>
                    {s.channel === 'email' && (
                        <input disabled={readOnly} className={`${inp} w-full mt-2`} placeholder="Subject" maxLength={200} value={s.subject || ''}
                            onChange={(e) => set(i, { subject: e.target.value })} />
                    )}
                    <textarea disabled={readOnly} className={`${inp} w-full mt-2`} rows={s.channel === 'sms' ? 3 : 7} maxLength={s.channel === 'sms' ? 600 : 8000}
                        placeholder={s.channel === 'sms' ? 'Text message (the brokerage name and "Reply STOP to opt out." are added)' : 'Email (the unsubscribe footer and postal address are added)'}
                        value={s.body} onChange={(e) => set(i, { body: e.target.value })} />
                    {lint && lint.steps && lint.steps[i] && <Findings findings={lint.steps[i].findings} />}
                </div>
            ))}
            {!readOnly && (
                <div className="flex gap-2">
                    <button className={btn} onClick={() => setSteps([...steps, blankStep('email')])}>+ Email step</button>
                    <button className={btn} onClick={() => setSteps([...steps, blankStep('sms')])}>+ Text step</button>
                </div>
            )}
            <p className="text-xs text-slate-500">Merge fields: {Object.entries(meta.merge_fields).map(([k, l]) => <span key={k} title={l} className="mr-2"><code>{`{${k}}`}</code></span>)}</p>
        </div>
    );
}

function DetailsForm({ meta, form, setForm, readOnly }) {
    const cfg = form.trigger_config || {};
    const setCfg = (patch) => setForm({ ...form, trigger_config: { ...cfg, ...patch } });
    const stops = form.stop_on || [];
    return (
        <div className="grid gap-3 md:grid-cols-2 text-sm">
            <label className="block">Name<input disabled={readOnly} className={`${inp} w-full`} maxLength={150} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="block">Who it is for
                <select disabled={readOnly} className={`${inp} w-full`} value={form.audience_type || ''} onChange={(e) => setForm({ ...form, audience_type: e.target.value })}>
                    {Object.entries(meta.audiences).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
            </label>
            <label className="block md:col-span-2">Description (staff only)<input disabled={readOnly} className={`${inp} w-full`} maxLength={1000} value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <label className="block">What enrols people
                <select disabled={readOnly} className={`${inp} w-full`} value={form.trigger_type || 'manual'} onChange={(e) => setForm({ ...form, trigger_type: e.target.value, trigger_config: {} })}>
                    {Object.entries(meta.triggers).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
            </label>
            <div>
                {form.trigger_type === 'lead_created' && (
                    <label className="block">Only leads from these sources (comma-separated; blank = any)
                        <input disabled={readOnly} className={`${inp} w-full`} value={(cfg.sources || []).join(', ')} onChange={(e) => setCfg({ sources: e.target.value.split(',') })} /></label>
                )}
                {form.trigger_type === 'tag_added' && (
                    <>
                        <label className="block">Tag<input disabled={readOnly} className={`${inp} w-full`} maxLength={60} value={cfg.tag || ''} onChange={(e) => setCfg({ tag: e.target.value })} /></label>
                        <label className="flex items-center gap-1 mt-1"><input disabled={readOnly} type="checkbox" checked={!!cfg.enrol_existing} onChange={(e) => setCfg({ enrol_existing: e.target.checked })} />
                            Also enrol people who already have the tag</label>
                    </>
                )}
                {form.trigger_type === 'deal_anniversary' && (
                    <div className="flex flex-wrap gap-2 items-center">
                        <label>Days after closing <input disabled={readOnly} type="number" min="1" max="3650" className={`${inp} w-20`} value={cfg.days_after_close ?? 365}
                            onChange={(e) => setCfg({ days_after_close: Number(e.target.value) })} /></label>
                        <label className="flex items-center gap-1"><input disabled={readOnly} type="checkbox" checked={cfg.repeat_yearly !== false}
                            onChange={(e) => setCfg({ repeat_yearly: e.target.checked })} /> every year</label>
                    </div>
                )}
                {form.trigger_type === 'manual' && <p className="text-xs text-slate-500 mt-5">People are enrolled by hand below.</p>}
            </div>
            <div className="md:col-span-2">
                <div>Stop the campaign for a person when:</div>
                <div className="flex flex-wrap gap-3 mt-1">
                    {Object.entries(meta.stop_conditions).map(([k, l]) => {
                        const fixed = meta.mandatory_stops.includes(k);
                        return (
                            <label key={k} className="flex items-center gap-1">
                                <input type="checkbox" disabled={readOnly || fixed} checked={fixed || stops.includes(k)}
                                    onChange={(e) => setForm({ ...form, stop_on: e.target.checked ? [...stops, k] : stops.filter((x) => x !== k) })} />
                                {l}{fixed ? ' (always)' : ''}
                            </label>
                        );
                    })}
                </div>
            </div>
            <label className="block">Kind of message
                <select disabled={readOnly} className={`${inp} w-full`} value={form.purpose || 'marketing'} onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
                    <option value="marketing">Marketing (consent and unsubscribe rules for marketing)</option>
                    <option value="service">Service (existing clients, about their own matter)</option>
                </select>
            </label>
            <label className="block">Skip a waiting text after (days)
                <input disabled={readOnly} type="number" min="1" max="60" className={`${inp} w-20 block`} value={form.sms_hold_max_days ?? 7}
                    onChange={(e) => setForm({ ...form, sms_hold_max_days: Number(e.target.value) })} /></label>
        </div>
    );
}

function Enrolments({ api, meta, campaign }) {
    const [rows, setRows] = useState([]);
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const [q, setQ] = useState('');
    const [found, setFound] = useState([]);
    const [pick, setPick] = useState({});
    const [hist, setHist] = useState(null);
    const id = campaign.campaign_id;
    const load = useCallback(async () => {
        try { setRows(await api.get(`/api/campaigns/${id}/enrolments`)); setErr(''); } catch (e) { setErr(e.message); }
    }, [api, id]);
    useEffect(() => { load(); }, [load]);
    const search = async () => {
        try { setFound(await api.get(`/api/campaigns/contacts?q=${encodeURIComponent(q)}`)); setPick({}); } catch (e) { setErr(e.message); }
    };
    const enrol = async () => {
        const ids = Object.keys(pick).filter((k) => pick[k]).map(Number);
        if (!ids.length) return;
        try {
            const r = await api.post(`/api/campaigns/${id}/enrol`, { contact_ids: ids });
            setMsg(`${r.enrolled} enrolled${r.already ? `, ${r.already} already in this campaign` : ''}${r.refused.length ? `, ${r.refused.length} refused (${r.refused.map((x) => x.reason).join('; ')})` : ''}.`);
            setPick({}); load();
        } catch (e) { setErr(errText(e)); }
    };
    const act = async (e, action) => {
        if (action === 'remove' && !window.confirm(`Remove ${e.name} from this campaign? Nothing more will be created for them.`)) return;
        try { await api.post(`/api/campaigns/enrolments/${e.enrolment_id}/${action}`, {}); load(); } catch (er) { setErr(errText(er)); }
    };
    const history = async (e) => {
        if (hist && hist.id === e.enrolment_id) { setHist(null); return; }
        try { setHist({ id: e.enrolment_id, rows: await api.get(`/api/campaigns/enrolments/${e.enrolment_id}/history`) }); } catch (er) { setErr(er.message); }
    };
    const canEnrol = meta.access.enrol && campaign.campaign_status !== 'archived';
    return (
        <div className="space-y-3">
            {err && <p className="text-sm text-rose-700">{err}</p>}
            {msg && <p className="text-sm text-emerald-700">{msg}</p>}
            {canEnrol && (
                <div className="p-3 border rounded bg-slate-50 text-sm space-y-2">
                    <div className="flex gap-2">
                        <input className={`${inp} flex-1`} placeholder="Find people in your book (name, email, phone)" value={q} onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') search(); }} />
                        <button className={btn} onClick={search}>Search</button>
                    </div>
                    {found.length > 0 && (
                        <>
                            <div className="max-h-48 overflow-auto border rounded bg-white">
                                {found.map((k) => (
                                    <label key={k.contact_id} className="flex items-center gap-2 px-2 py-1 border-b">
                                        <input type="checkbox" checked={!!pick[k.contact_id]} onChange={(e) => setPick({ ...pick, [k.contact_id]: e.target.checked })} />
                                        {k.name} <span className="text-xs text-slate-500">{[k.has_email ? 'email' : null, k.has_phone ? 'phone' : null].filter(Boolean).join(' + ') || 'no email or phone'}</span>
                                    </label>
                                ))}
                            </div>
                            <button className={btnDark} onClick={enrol}>Enrol selected</button>
                        </>
                    )}
                    {campaign.campaign_status !== 'active' && <p className="text-xs text-amber-700">The campaign is {campaign.campaign_status}: people enrolled now get nothing until it is active.</p>}
                </div>
            )}
            <DataList rows={rows} rowKey="enrolment_id" csvName={`campaign-${id}-enrolled`} defaultSort={{ key: 'name', dir: 'asc' }} empty="Nobody enrolled yet."
                filters={[{ key: 'enrol_status', label: 'Status', options: ['active', 'paused', 'completed', 'stopped', 'removed'] }]}
                rowClass={(e) => (hist && hist.id === e.enrolment_id ? 'bg-slate-50' : '')}
                columns={[
                    { key: 'name', label: 'Person', render: (e) => <Link className="text-blue-700 underline" to={`/dashboard/crm/contacts/${e.contact_id}`}>{e.name}</Link> },
                    { key: 'enrol_status', label: 'Status', render: (e) => <Badge s={e.enrol_status} /> },
                    { key: 'step', label: 'Step', type: 'number', value: (e) => Number(e.current_step) + 1 },
                    { key: 'next_due_at', label: 'Next due', type: 'date', value: (e) => (e.enrol_status === 'active' ? e.next_due_at : ''), render: (e) => (e.enrol_status === 'active' ? when(e.next_due_at) : '') },
                    { key: 'note', label: 'Note', value: (e) => e.stop_reason || e.hold_reason || '', render: (e) => <span className="text-xs text-slate-600">{e.stop_reason || e.hold_reason || ''}</span> },
                    { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (e) => (
                        <span className="whitespace-nowrap">
                            <button className={btn} onClick={() => history(e)}>{hist && hist.id === e.enrolment_id ? 'Hide history' : 'History'}</button>{' '}
                            {meta.access.enrol && e.enrol_status === 'active' && <button className={btn} onClick={() => act(e, 'pause')}>Pause</button>}
                            {meta.access.enrol && e.enrol_status === 'paused' && <button className={btn} onClick={() => act(e, 'resume')}>Resume</button>}{' '}
                            {meta.access.enrol && ['active', 'paused'].includes(e.enrol_status) && <button className={btn} onClick={() => act(e, 'remove')}>Remove</button>}
                        </span>) },
                ]} />
            {hist && (
                <div className="p-2 bg-slate-50 border rounded">
                    <div className="text-xs font-semibold mb-1">History — {(rows.find((r) => r.enrolment_id === hist.id) || {}).name || ''}</div>
                    <ul className="text-xs space-y-0.5">
                        {hist.rows.map((h) => <li key={h.event_id}>{when(h.created_at)} — {h.event_type}{h.step_order ? ` (step ${h.step_order})` : ''}{h.detail ? `: ${h.detail}` : ''}{h.msg_status ? ` [Outbox: ${h.msg_status}]` : ''}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}

function Preview({ api, campaignId }) {
    const [p, setP] = useState(null);
    const [err, setErr] = useState('');
    const [q, setQ] = useState('');
    const [found, setFound] = useState([]);
    const load = useCallback(async (contactId) => {
        try { setP(await api.get(`/api/campaigns/${campaignId}/preview${contactId ? `?contact_id=${contactId}` : ''}`)); setErr(''); } catch (e) { setErr(e.message); }
    }, [api, campaignId]);
    useEffect(() => { load(null); }, [load]);
    const search = async () => { try { setFound(await api.get(`/api/campaigns/contacts?q=${encodeURIComponent(q)}`)); } catch (e) { setErr(e.message); } };
    return (
        <div className="space-y-3 text-sm">
            {err && <p className="text-rose-700">{err}</p>}
            <div className="flex flex-wrap gap-2 items-center">
                <span>Preview for: <b>{p ? p.who : '…'}</b></span>
                <input className={inp} placeholder="Find a contact" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') search(); }} />
                <button className={btn} onClick={search}>Search</button>
                <button className={btn} onClick={() => { setFound([]); load(null); }}>Sample values</button>
            </div>
            {found.length > 0 && (
                <div className="flex flex-wrap gap-1">{found.slice(0, 20).map((k) => <button key={k.contact_id} className={btn} onClick={() => { setFound([]); load(k.contact_id); }}>{k.name}</button>)}</div>
            )}
            {p && p.steps.map((s) => (
                <div key={s.step_id} className="p-3 border rounded bg-white">
                    <div className="text-xs text-slate-500">Step {s.step_order} · {s.channel === 'sms' ? 'Text' : 'Email'} · wait {s.wait_days} day(s){Number(s.is_active) ? '' : ' · switched off'}</div>
                    {s.subject_rendered && <div className="font-semibold mt-1">{s.subject_rendered}</div>}
                    <pre className="whitespace-pre-wrap font-sans mt-1">{s.body_rendered}</pre>
                    {s.missing.length > 0 && <p className="text-xs text-amber-700">Not on file for this person (a fallback is used): {s.missing.join(', ')}</p>}
                    <Findings findings={s.findings} />
                </div>
            ))}
            <p className="text-xs text-slate-500">The Outbox adds the brokerage name and "Reply STOP to opt out." to texts, and the identification block, postal address and unsubscribe link to emails.</p>
        </div>
    );
}

function CampaignEditor({ api, meta, id, onBack }) {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [steps, setSteps] = useState([]);
    const [lint, setLint] = useState(null);
    const [tab, setTab] = useState('setup');
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const [dirty, setDirty] = useState(false);
    const manage = meta.access.manage;
    const isBroker = meta.access.broker;
    const load = useCallback(async () => {
        try {
            const d = await api.get(`/api/campaigns/${id}`);
            setData(d); setForm(d.campaign); setSteps(d.steps.map((s) => ({ ...s }))); setLint(d.lint); setDirty(false); setErr('');
        } catch (e) { setErr(e.message); }
    }, [api, id]);
    useEffect(() => { load(); }, [load]);
    if (!data || !form) return <div>{err ? <p className="text-sm text-rose-700">{err}</p> : <p className="text-sm text-slate-500">Loading…</p>}</div>;
    const c = data.campaign;
    const readOnly = !manage || c.campaign_status === 'archived';
    const wrap = async (fn, ok) => { setErr(''); setMsg(''); try { await fn(); if (ok) setMsg(ok); await load(); } catch (e) { setErr(errText(e)); if (e.payload && e.payload.lint) setLint(e.payload.lint); } };
    const save = () => wrap(async () => {
        if (c.campaign_status === 'active' && data.wording.approved && !window.confirm('This campaign is active. If you change the wording, it stops creating messages until the managing broker approves the new wording. Save?')) throw new Error('Not saved.');
        await api.put(`/api/campaigns/${id}`, { name: form.name, description: form.description, audience_type: form.audience_type, trigger_type: form.trigger_type,
            trigger_config: form.trigger_config, stop_on: form.stop_on, purpose: form.purpose, sms_hold_max_days: form.sms_hold_max_days });
        await api.put(`/api/campaigns/${id}/steps`, { steps });
    }, 'Saved.');
    const check = async () => { try { setLint(await api.post('/api/campaigns/lint', { steps })); } catch (e) { setErr(errText(e)); } };
    const status = (s, confirmText) => { if (confirmText && !window.confirm(confirmText)) return; wrap(() => api.post(`/api/campaigns/${id}/status`, { status: s }), `Campaign ${s === 'active' ? 'activated' : s}.`); };
    const approve = (withdraw) => {
        if (!withdraw && !window.confirm('Approve this wording exactly as it is now? Every message this campaign creates will use it. Any later edit needs your approval again.')) return;
        wrap(() => api.post(`/api/campaigns/${id}/approve-wording`, { withdraw }), withdraw ? 'Approval withdrawn.' : 'Wording approved.');
    };
    const autoSend = (on) => {
        if (on && !window.confirm('Turn auto-send ON for this campaign? Its messages will then leave without a person approving each one — only if the Outbox\'s own automatic sending is also on. Contact Compliance still checks every message.')) return;
        wrap(() => api.post(`/api/campaigns/${id}/auto-send`, { on, confirm: true }), on ? 'Auto-send is on.' : 'Auto-send is off: messages wait in the Outbox.');
    };
    const del = () => {
        if (!window.confirm(`Delete "${c.name}"? Everyone in it is stopped. Messages already created stay in the Outbox.`)) return;
        wrap(async () => { await api.delete(`/api/campaigns/${id}`); onBack(); });
    };
    const setStepsDirty = (s) => { setSteps(s); setDirty(true); };
    const setFormDirty = (f) => { setForm(f); setDirty(true); };
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <button className={btn} onClick={onBack}>← All campaigns</button>
                <h2 className="text-lg font-semibold">{c.name}</h2>
                <Badge s={c.campaign_status} />
                {Number(c.auto_send) ? <Badge s="active">auto-send on</Badge> : <Badge s="draft">auto-send off — approval in Outbox</Badge>}
            </div>
            {err && <p className="text-sm text-rose-700">{err}</p>}
            {msg && <p className="text-sm text-emerald-700">{msg}</p>}

            <div className="p-3 border rounded bg-slate-50 text-sm space-y-2">
                <div>
                    <b>Wording: </b>
                    {data.wording.approved ? <span className="text-emerald-700">approved by the managing broker {when(data.wording.approved_at)}</span>
                        : data.wording.changed ? <span className="text-rose-700">changed since the managing broker approved it — nothing is created until he approves it again</span>
                            : <span className="text-amber-700">not yet approved by the managing broker</span>}
                </div>
                {data.problems.length > 0 && <div className="text-amber-800">Before it can be approved: {data.problems.join(' ')}</div>}
                <div className="flex flex-wrap gap-2">
                    {isBroker && !data.wording.approved && <button className={btnGreen} disabled={dirty || data.problems.length > 0} onClick={() => approve(false)}>Approve wording</button>}
                    {isBroker && c.wording_approved_hash && <button className={btn} onClick={() => approve(true)}>Withdraw approval</button>}
                    {manage && ['draft', 'paused'].includes(c.campaign_status) && <button className={btnDark} disabled={dirty || !data.wording.approved} onClick={() => status('active', 'Activate this campaign? People are enrolled by its trigger from now on, and messages are created in the Outbox when the site switch is on.')}>
                        {c.campaign_status === 'paused' ? 'Resume' : 'Activate'}</button>}
                    {manage && c.campaign_status === 'active' && <button className={btn} onClick={() => status('paused')}>Pause</button>}
                    {manage && c.campaign_status !== 'archived' && <button className={btn} onClick={() => status('archived', 'Archive this campaign? Everyone in it is stopped.')}>Archive</button>}
                    {manage && c.campaign_status === 'archived' && <button className={btn} onClick={() => status('draft')}>Back to draft</button>}
                    {isBroker && c.campaign_status === 'active' && (Number(c.auto_send)
                        ? <button className={btn} onClick={() => autoSend(false)}>Turn auto-send off</button>
                        : <button className={btn} onClick={() => autoSend(true)}>Turn auto-send on</button>)}
                    {manage && <button className={btn} onClick={del}>Delete</button>}
                </div>
                {dirty && <p className="text-xs text-amber-700">You have unsaved changes; save before approving or activating.</p>}
            </div>

            <div className="flex gap-1 border-b">
                {[['setup', 'Setup & steps'], ['preview', 'Preview'], ['people', 'People enrolled']].map(([k, l]) => (
                    <button key={k} className={`px-3 py-1 text-sm ${tab === k ? 'border-b-2 border-slate-800 font-semibold' : 'text-slate-600'}`} onClick={() => setTab(k)}>{l}</button>
                ))}
            </div>
            {tab === 'setup' && (
                <div className="space-y-4">
                    <DetailsForm meta={meta} form={form} setForm={setFormDirty} readOnly={readOnly} />
                    <h3 className="font-semibold">Steps</h3>
                    <StepEditor meta={meta} steps={steps} setSteps={setStepsDirty} readOnly={readOnly} lint={lint} />
                    {!readOnly && (
                        <div className="flex gap-2">
                            <button className={btn} onClick={check}>Check wording</button>
                            <button className={btnDark} onClick={save}>Save</button>
                            {dirty && <button className={btn} onClick={load}>Discard changes</button>}
                        </div>
                    )}
                    {data.stats && (
                        <p className="text-xs text-slate-600">Numbers: {data.stats.enrolled} enrolled · {data.stats.active} active · {data.stats.completed} completed ·
                            {' '}{data.stats.sent} sent · {data.stats.awaiting_approval} awaiting approval · {data.stats.held} held (quiet hours / limits) · {data.stats.blocked} blocked by Contact Compliance
                            · {data.stats.failed} failed · {data.stats.replied} replied · {data.stats.unsubscribed} opted out · opens are not tracked.</p>
                    )}
                </div>
            )}
            {tab === 'preview' && <Preview api={api} campaignId={id} />}
            {tab === 'people' && <Enrolments api={api} meta={meta} campaign={c} />}
        </div>
    );
}

// ---------------------------------------------------------------------------
// The newsletter
// ---------------------------------------------------------------------------
function Newsletter({ api, meta }) {
    const [issues, setIssues] = useState([]);
    const [settings, setSettings] = useState(null);
    const [open, setOpen] = useState(null);
    const [edit, setEdit] = useState({ subject: '', intro: '' });
    const [err, setErr] = useState('');
    const [msg, setMsg] = useState('');
    const isBroker = meta.access.broker;
    const load = useCallback(async () => {
        try { setIssues(await api.get('/api/campaigns/newsletter/issues')); setSettings(await api.get('/api/campaigns/newsletter/settings')); setErr(''); } catch (e) { setErr(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);
    const openIssue = async (iid) => {
        try { const r = await api.get(`/api/campaigns/newsletter/issues/${iid}`); setOpen(r); setEdit({ subject: r.issue.subject || '', intro: r.issue.intro || '' }); } catch (e) { setErr(e.message); }
    };
    const wrap = async (fn, ok) => {
        setErr(''); setMsg('');
        try { const r = await fn(); if (r && r.issue) { setOpen(r); setEdit({ subject: r.issue.subject || '', intro: r.issue.intro || '' }); } if (ok) setMsg(typeof ok === 'function' ? ok(r) : ok); load(); }
        catch (e) { setErr(errText(e)); }
    };
    const start = () => wrap(() => api.post('/api/campaigns/newsletter/issues', {}), 'Draft started.');
    const saveSettings = () => wrap(() => api.put('/api/campaigns/newsletter/settings', settings), 'Settings saved.');
    const i = open && open.issue;
    const editable = i && ['draft', 'approved'].includes(i.issue_status);
    return (
        <div className="space-y-4 text-sm">
            {err && <p className="text-rose-700">{err}</p>}
            {msg && <p className="text-emerald-700">{msg}</p>}
            {!i && (
                <>
                    <div className="flex gap-2"><button className={btnDark} onClick={start}>Start this month's draft</button></div>
                    <DataList rows={issues} rowKey="issue_id" csvName="newsletter-issues" defaultSort={{ key: 'issue_month', dir: 'desc' }} empty="No newsletter issues yet."
                        onRowClick={(x) => openIssue(x.issue_id)} filters={[{ key: 'issue_status', label: 'Status' }]}
                        columns={[
                            { key: 'issue_month', label: 'Month' },
                            { key: 'subject', label: 'Subject' },
                            { key: 'issue_status', label: 'Status', render: (x) => <Badge s={x.issue_status} /> },
                            { key: 'copies', label: 'Copies', type: 'number' },
                        ]} />
                    {settings && (
                        <div className="p-3 border rounded space-y-2">
                            <h3 className="font-semibold">Newsletter settings{isBroker ? '' : ' (the managing broker changes these)'}</h3>
                            <label className="flex items-center gap-1"><input type="checkbox" disabled={!isBroker} checked={!!Number(settings.auto_draft)} onChange={(e) => setSettings({ ...settings, auto_draft: e.target.checked ? 1 : 0 })} />
                                Start each month's draft automatically (it still waits for approval)</label>
                            <label className="block">Areas to report (one city, ZIP or county per line; blank = the website's service cities)
                                <textarea disabled={!isBroker} rows={3} className={`${inp} w-full`} value={settings.service_areas || ''} onChange={(e) => setSettings({ ...settings, service_areas: e.target.value })} /></label>
                            <label className="block">Subscriber tag (contacts with this CRM tag receive it)
                                <input disabled={!isBroker} className={inp} value={settings.subscriber_tag || ''} onChange={(e) => setSettings({ ...settings, subscriber_tag: e.target.value })} /></label>
                            <label className="flex items-center gap-1"><input type="checkbox" disabled={!isBroker} checked={!!Number(settings.include_express_consent)}
                                onChange={(e) => setSettings({ ...settings, include_express_consent: e.target.checked ? 1 : 0 })} /> Also send to contacts whose email has express consent on file</label>
                            <div className="flex gap-3">
                                <label>Copies per run <input disabled={!isBroker} type="number" min="10" max="1000" className={`${inp} w-24`} value={settings.batch_size}
                                    onChange={(e) => setSettings({ ...settings, batch_size: Number(e.target.value) })} /></label>
                                <label>New listings shown <input disabled={!isBroker} type="number" min="0" max="20" className={`${inp} w-20`} value={settings.max_new_listings}
                                    onChange={(e) => setSettings({ ...settings, max_new_listings: Number(e.target.value) })} /></label>
                            </div>
                            {isBroker && <button className={btnDark} onClick={saveSettings}>Save settings</button>}
                        </div>
                    )}
                </>
            )}
            {i && (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <button className={btn} onClick={() => setOpen(null)}>← All issues</button>
                        <b>{i.issue_month}</b><Badge s={i.issue_status} />
                        <span className="text-slate-500">{open.recipients} recipient(s) still to receive it</span>
                    </div>
                    <label className="block">Subject<input disabled={!editable} className={`${inp} w-full`} maxLength={200} value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} /></label>
                    <label className="block">Introduction<textarea disabled={!editable} rows={6} className={`${inp} w-full`} value={edit.intro} onChange={(e) => setEdit({ ...edit, intro: e.target.value })} /></label>
                    {editable && (
                        <div className="flex flex-wrap gap-2">
                            <button className={btnDark} onClick={() => wrap(() => api.put(`/api/campaigns/newsletter/issues/${i.issue_id}`, edit), 'Saved (back to draft).')}>Save</button>
                            <button className={btn} onClick={() => wrap(() => api.put(`/api/campaigns/newsletter/issues/${i.issue_id}`, { ...edit, refresh: true }), 'Figures refreshed.')}>Refresh the figures</button>
                            {isBroker && i.issue_status === 'draft' && <button className={btnGreen} onClick={() => { if (window.confirm('Approve this issue? It is then sent in batches by the daily run.')) wrap(() => api.post(`/api/campaigns/newsletter/issues/${i.issue_id}/approve`, {}), 'Approved.'); }}>Approve</button>}
                        </div>
                    )}
                    {isBroker && ['approved', 'sending'].includes(i.issue_status) && (
                        <div className="flex gap-2">
                            <button className={btn} onClick={() => wrap(() => api.post(`/api/campaigns/newsletter/issues/${i.issue_id}/send-batch`, {}), (r) => `${r.handed} copies handed to the Outbox; ${r.remaining} to go.`)}>Send the next batch now</button>
                            <button className={btn} onClick={() => { if (window.confirm('Stop sending this issue?')) wrap(() => api.post(`/api/campaigns/newsletter/issues/${i.issue_id}/cancel`, {}), 'Cancelled.'); }}>Stop sending</button>
                        </div>
                    )}
                    {i.lint_result && <Findings findings={i.lint_result.findings} />}
                    <div className="border rounded">
                        <div className="px-2 py-1 text-xs text-slate-500 border-b">Preview (the unsubscribe footer and postal address are added to each copy)</div>
                        <iframe title="Newsletter preview" sandbox="" srcDoc={i.body_html || ''} className="w-full h-96 bg-white" />
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------
function Settings({ api, meta, reload }) {
    const [err, setErr] = useState('');
    const on = !!Number(meta.settings.runner_enabled);
    const flip = async () => {
        if (!on && !window.confirm(`Switch campaigns ON for this site (${meta.settings.site === 'FL' ? 'Florida' : 'Illinois'})? The daily run will enrol people into active campaigns and create their messages in the Outbox.`)) return;
        try { await api.put('/api/campaigns/settings', { runner_enabled: !on, confirm: true }); reload(); } catch (e) { setErr(errText(e)); }
    };
    return (
        <div className="space-y-4 text-sm">
            {err && <p className="text-rose-700">{err}</p>}
            <div className="p-3 border rounded space-y-2">
                <h3 className="font-semibold">Campaigns on this site ({meta.settings.site === 'FL' ? 'Florida' : 'Illinois'})</h3>
                <p>Status: <b className={on ? 'text-emerald-700' : 'text-slate-700'}>{on ? 'ON' : 'OFF'}</b>
                    {meta.settings.runner_changed_at ? ` — changed ${when(meta.settings.runner_changed_at)}` : ''}</p>
                <p className="text-slate-600">While OFF, the daily run neither enrols anyone nor creates any message. Each site (Illinois, Florida) has its own switch, and both start OFF.
                    {meta.settings.site === 'FL' ? ' On the Florida site every text also follows Florida\'s 8 a.m.–8 p.m. hours and the 3-messages-per-24-hours rule (the Outbox holds, never drops, a text outside them).' : ''}</p>
                {meta.access.broker ? <button className={on ? btn : btnDark} onClick={flip}>{on ? 'Switch campaigns OFF' : 'Switch campaigns ON'}</button>
                    : <p className="text-slate-500">Only the managing broker changes this.</p>}
                {meta.settings.unavailable && <p className="text-amber-700">The campaigns tables are not installed on this site yet.</p>}
            </div>
            <div className="p-3 border rounded space-y-1">
                <h3 className="font-semibold">What else decides whether a message leaves</h3>
                <p>1. Each campaign's <b>auto-send</b> (managing broker, on the campaign). Off = every message waits in the Outbox for a person to approve.</p>
                <p>2. The Outbox's own automatic sending: email {meta.outbox.auto_send_email ? 'ON' : 'OFF'}, texts {meta.outbox.auto_send_sms ? 'ON' : 'OFF'}{meta.outbox.require_approval ? ', and "approval required for everything" is ON' : ''}.</p>
                <p>3. Texts: {meta.sms.ready ? <span className="text-emerald-700">ready</span> : <span className="text-amber-700">waiting — {meta.sms.reason}</span>}. The 10DLC box is in Website Chat → Outbox → Settings.</p>
                <p>4. Contact Compliance checks every message (consent, do-not-contact, quiet hours, unsubscribes and STOP replies).</p>
                <p><Link className="text-blue-700 underline" to="/dashboard/website-chat">Open the Outbox</Link></p>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
export default function CampaignsPage() {
    const api = useApiClient();
    const [meta, setMeta] = useState(null);
    const [err, setErr] = useState('');
    const [tab, setTab] = useState('campaigns');
    const [openId, setOpenId] = useState(null);
    const loadMeta = useCallback(async () => {
        try { setMeta(await api.get('/api/campaigns/meta')); setErr(''); } catch (e) { setErr(e.message); }
    }, [api]);
    useEffect(() => { loadMeta(); }, [loadMeta]);
    if (!meta) return <div className="p-4">{err ? <p className="text-rose-700">{err}</p> : <p className="text-slate-500">Loading…</p>}</div>;
    const tabs = [['campaigns', 'Campaigns'], ...(meta.access.manage ? [['newsletter', 'Newsletter']] : []), ['settings', 'Settings']];
    return (
        <div className="p-4 space-y-4">
            <div>
                <h1 className="text-xl font-semibold">Campaigns</h1>
                <p className="text-sm text-slate-600">Drip emails and texts, and the monthly newsletter. Every message goes through the Outbox and Contact Compliance.</p>
            </div>
            {!Number(meta.settings.runner_enabled) && (
                <div className="p-2 rounded bg-amber-50 border border-amber-200 text-sm text-amber-900">
                    Campaigns are switched OFF on this site: nothing is enrolled or created. {meta.access.broker ? 'Turn them on in the Settings tab.' : 'The managing broker turns them on.'}
                </div>
            )}
            {!meta.sms.ready && <div className="p-2 rounded bg-slate-50 border text-sm text-slate-700">Text steps wait: {meta.sms.reason}.</div>}
            <div className="flex gap-1 border-b">
                {tabs.map(([k, l]) => (
                    <button key={k} className={`px-3 py-1 text-sm ${tab === k ? 'border-b-2 border-slate-800 font-semibold' : 'text-slate-600'}`} onClick={() => { setTab(k); setOpenId(null); }}>{l}</button>
                ))}
            </div>
            {tab === 'campaigns' && (openId
                ? <CampaignEditor api={api} meta={meta} id={openId} onBack={() => setOpenId(null)} />
                : <CampaignList api={api} meta={meta} onOpen={setOpenId} />)}
            {tab === 'newsletter' && <Newsletter api={api} meta={meta} />}
            {tab === 'settings' && <Settings api={api} meta={meta} reload={loadMeta} />}
        </div>
    );
}
